import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Video, LogOut, Building2, Loader2, CalendarClock, BadgeCheck, Users, PhoneOutgoing, TrendingUp, CheckCircle2, EyeOff, Award } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProviderCareFeed from "@/components/ProviderCareFeed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_BADGE = {
  listed:     "bg-[#F1F5F9] text-[#475569]",
  essential:  "bg-[#EDF7F6] text-primary",
  enhanced:   "bg-[#E0F2F1] text-[#0F766E]",
  premium:    "bg-[#EDE9FE] text-[#5B21B6]",
  enterprise: "bg-[#FEF3C7] text-[#92400E]",
};

export default function ProviderDashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsEmail = searchParams.get("as");
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/promo`).then((res) => setPromo(res.data)).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/provider/consults`, {
        params: viewAsEmail ? { as_email: viewAsEmail } : {}
      });
      setData(res.data);
    } catch (error) {
      console.error("Error loading provider dashboard:", error);
    }
  }, [viewAsEmail]);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (user) loadData();
  }, [user, loading, navigate, loadData]);

  if (loading || !user || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const now = new Date();
  const isJoinable = (c) => {
    const start = new Date(c.scheduled_at);
    const opens = new Date(start.getTime() - 10 * 60000);
    const closes = new Date(start.getTime() + (c.duration_minutes + 30) * 60000);
    return c.status !== "cancelled" && now >= opens && now <= closes;
  };
  const isPast = (c) => now > new Date(new Date(c.scheduled_at).getTime() + (c.duration_minutes + 30) * 60000);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button data-testid="provider-dash-back-btn" onClick={() => navigate("/")} variant="ghost" className="text-primary hover:bg-primary/5 px-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-heading font-bold text-primary">Provider Dashboard</h1>
            </div>
          </div>
          <Button
            data-testid="provider-dash-logout-btn"
            onClick={async () => { await logout(); navigate("/"); }}
            variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-body font-semibold"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Admin view-as banner */}
        {viewAsEmail && user.role === "admin" && (
          <div data-testid="view-as-banner" className="flex flex-wrap items-center justify-between gap-3 bg-primary text-white rounded-xl px-5 py-3 font-body text-sm">
            <span>👁 Admin view — you're seeing the dashboard of <strong>{viewAsEmail}</strong></span>
            <Button onClick={() => navigate("/admin")} variant="outline" className="border-white/50 text-white bg-transparent hover:bg-white/10 rounded-full font-semibold h-8 text-xs">
              Back to Company Dashboard
            </Button>
          </div>
        )}

        {/* Upgrade to Premium CTA — provider-facing only, no ranking claims */}
        {data.upsell?.show && (
          <section
            data-testid="premium-upsell-banner"
            className="rounded-2xl p-6 md:p-8 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-2 border-primary/40 shadow-md"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-xl">
                <p className="text-xs font-body font-bold uppercase tracking-wide text-primary mb-1">Premium feature</p>
                <h2 className="text-2xl font-heading font-bold text-text-primary mb-2">Upgrade to Premium</h2>
                <p className="font-body text-text-secondary">
                  Premium providers offered <strong className="text-text-primary">{data.upsell.premium_consults_30d} virtual consult{data.upsell.premium_consults_30d !== 1 ? "s" : ""}</strong> to
                  families in the last 30 days. Premium unlocks in-app video consult booking, intro video, a photo gallery, and
                  contribution to the Tyvena educational library. Your current listing doesn&apos;t include video consults.
                </p>
                {promo?.enabled && (
                  <p data-testid="upsell-promo-text" className="font-body font-bold text-primary mt-2">
                    {promo.title}: {promo.description}
                  </p>
                )}
              </div>
              <Button
                data-testid="premium-upsell-btn"
                onClick={() => navigate("/provider-onboarding")}
                className="min-h-[48px] px-8 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-lg font-body"
              >
                <Award className="w-5 h-5 mr-2" /> Upgrade to Premium
              </Button>
            </div>
          </section>
        )}

        {/* ROI Stats */}
        <section data-testid="provider-stats-section" className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Families Chose You", value: data.stats?.total_leads ?? 0, icon: Users, testid: "stat-total-leads" },
            { label: "Open to Recontact", value: data.stats?.recontactable_leads ?? 0, icon: PhoneOutgoing, testid: "stat-recontactable" },
            { label: "Total Consults", value: data.stats?.total_consults ?? 0, icon: Video, testid: "stat-total-consults" },
            { label: "Completed Consults", value: data.stats?.completed_consults ?? 0, icon: CheckCircle2, testid: "stat-completed-consults" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-5">
              <s.icon className="w-5 h-5 text-primary mb-2" />
              <p className="text-3xl font-heading font-bold text-text-primary">{s.value}</p>
              <p className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wide mt-1">{s.label}</p>
            </div>
          ))}
        </section>

        {/* Families Who Chose You (ROI leads) */}
        <section data-testid="provider-leads-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Families Who Chose You</h2>
          </div>
          {(!data.leads || data.leads.length === 0) ? (
            <p className="text-text-secondary font-body py-4">
              When families add your listing to their care plan, they'll appear here so you can track your return on investment.
            </p>
          ) : (
            <div className="space-y-2">
              {data.leads.map((lead, i) => (
                <div key={i} data-testid={`provider-lead-${i}`} className="flex flex-wrap items-center justify-between gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    {lead.anonymous ? (
                      <EyeOff className="w-5 h-5 text-text-secondary flex-shrink-0" />
                    ) : (
                      <Users className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                    <div>
                      <p className={`font-body font-bold ${lead.anonymous ? "text-text-secondary italic" : "text-text-primary"}`}>
                        {lead.name}
                      </p>
                      <p className="text-sm font-body text-text-secondary">
                        {lead.anonymous
                          ? "This family chose to keep their details private"
                          : <>{lead.care_recipient_name ? `Caring for ${lead.care_recipient_name} · ` : ""}{lead.email}</>}
                      </p>
                    </div>
                  </div>
                  {lead.allow_recontact ? (
                    <span data-testid={`lead-recontact-ok-${i}`} className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 flex items-center gap-1">
                      <PhoneOutgoing className="w-3 h-3" /> Open to recontact
                    </span>
                  ) : (
                    <span data-testid={`lead-no-contact-${i}`} className="px-3 py-1 rounded-full text-xs font-bold bg-[#E2E8F0] text-[#475569]">
                      Do not contact
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Listings */}
        <section data-testid="provider-listings-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <BadgeCheck className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">My Listings</h2>
          </div>
          {data.listings.length === 0 ? (
            <div className="py-4">
              <p className="text-text-secondary font-body mb-4">
                No listings are linked to <strong>{user.email}</strong> yet. Register your agency to appear in family searches.
              </p>
              <Button onClick={() => navigate("/provider-onboarding")} className="bg-primary text-white rounded-full font-bold font-body">
                Register My Agency
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {data.listings.map((l) => (
                <div key={l.id} className={`flex items-center justify-between gap-3 rounded-xl p-4 border ${l.is_active ? "bg-[#F8FAFC] border-[#E2E8F0]" : "bg-[#FEF3C7] border-[#FDE68A]"}`}>
                  <p className="font-body font-bold text-text-primary">{l.name}</p>
                  <div className="flex items-center gap-2">
                    {!l.is_active && (
                      <span data-testid={`listing-pending-${l.id}`} className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-[#FDE68A] text-[#92400E]">Pending Approval</span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${TIER_BADGE[l.tier] || TIER_BADGE.listed}`}>{l.tier}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Live Care Feed — anonymized activity in provider's service area */}
        <ProviderCareFeed viewAsEmail={viewAsEmail} />

        {/* Consults */}
        <section data-testid="provider-consults-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Video className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Virtual Consults</h2>
          </div>
          {data.consults.length === 0 ? (
            <p className="text-text-secondary font-body py-4">
              No consults yet. When families book a virtual consult with your premium listing, it will appear here.
            </p>
          ) : (
            <div className="space-y-3">
              {data.consults.map((c) => (
                <div key={c.id} data-testid={`provider-consult-${c.id}`} className="flex flex-wrap items-center justify-between gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div>
                    <p className="font-body font-bold text-text-primary flex items-center gap-2">
                      <CalendarClock className="w-4 h-4 text-primary" />
                      {new Date(c.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    <p className="text-sm font-body text-text-secondary">
                      With {c.family_name}{c.care_recipient_name ? ` (caring for ${c.care_recipient_name})` : ""} · {c.duration_minutes} min · {c.provider_name}
                    </p>
                  </div>
                  {c.status === "cancelled" ? (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">Cancelled</span>
                  ) : isJoinable(c) ? (
                    <Button
                      data-testid={`provider-join-consult-${c.id}`}
                      onClick={() => navigate(`/consult/join/${c.provider_join_code}`)}
                      className="bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
                    >
                      <Video className="w-4 h-4 mr-2" /> Join Now
                    </Button>
                  ) : isPast(c) ? (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#E2E8F0] text-[#475569]">Completed</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">Upcoming</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
